import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { first } from 'rxjs/operators';

import { SubscriptionPlan } from '@vi-iot/client';

import { BookingDashboardDevice, InstallationsStore } from '@store';

import { BookingActions } from './booking.actions';
import { BookingSelectors } from './booking.selectors';

@Injectable({ providedIn: 'root' })
export class BookingStore {
  subscriptionsPlans$ = this.store.select(BookingSelectors.selectSubscriptionsPlans());
  devicesCount$ = this.store.select(BookingSelectors.getDevicesCount());
  selectedDevices$ = this.store.select(BookingSelectors.selectSelectedDevices);
  selectedInstallationsCount$ = this.store.select(BookingSelectors.getSelectedInstallationsCount());
  selectedPlan$ = this.store.select(BookingSelectors.getSelectedPlan());
  selectedInstallationsSerials$ = this.store.select(
    BookingSelectors.getSelectedInstallationsSerials()
  );
  subscriptions$ = this.store.select(BookingSelectors.selectSubscriptions());
  trialSubscriptions$ = this.store.select(BookingSelectors.selectTrialSubscriptions());
  trialSubscriptionsActive$ = this.store.select(BookingSelectors.trialActiveSubscriptions);
  trialSubscriptionsActiveEndingSoon$ = this.store.select(
    BookingSelectors.trialActiveSubscriptionsEndingSoon
  );
  trialSubscriptionsActiveOnlineOnly$ = this.store.select(
    BookingSelectors.trialActiveSubscriptionsOnline
  );
  trialActiveSubscriptionsOfflineForBooking$ = this.store.select(
    BookingSelectors.trialActiveSubscriptionsOfflineForBooking
  );
  contingentSubscriptions$ = this.store.select(BookingSelectors.selectContingentSubscriptions());
  automaticExtensionSubscriptionsEndingSoon$ = this.store.select(
    BookingSelectors.automaticExtensionSubscriptionsEndingSoon
  );
  hasAccessToBooking$ = this.store.select(BookingSelectors.hasAccessToBooking());
  subscriptionsDetails$ = this.store.select(BookingSelectors.selectSubscriptionsDetails());
  subscriptionsDetailsLoaded$ = this.store.select(
    BookingSelectors.selectSubscriptionsDetailsLoadedState()
  );
  bookingPlansLoadedState$ = this.store.select(BookingSelectors.selectBookingPlansLoadedState());
  commercialBookingPlansLoadedState$ = this.store.select(
    BookingSelectors.selectCommercialBookingPlansLoadedState()
  );
  loaded$ = this.store.select(BookingSelectors.selectLoadedState());
  isLoading$ = this.store.select(BookingSelectors.selectIsLoadingState());
  bookingViewLoaded$ = this.store.select(BookingSelectors.selectBookingLoadedState());
  error$ = this.store.select(BookingSelectors.selectError());
  purchaseTrialStatus$ = this.store.select(BookingSelectors.selectPurchaseTrialStatus());
  buySubscriptionError$ = this.store.select(BookingSelectors.selectBuySubscriptionError());
  isSimulatingSubscription$ = this.store.select(BookingSelectors.isSimulatingSubscription());
  subscriptionSimulatedError$ = this.store.select(BookingSelectors.getSubscriptionSimulatedError());
  subscriptionSimulatedSuccesfully$ = this.store.select(
    BookingSelectors.getSubscriptionSimulatedSuccessfully()
  );
  isOnlyOnePlusSubscriptionSelectedToBeUpdatedToProPlan$ = this.store.select(
    BookingSelectors.isOnlyOnePlusSubscriptionSelectedToBeUpdatedToProPlan()
  );
  getCurrencyOfProPackage$ = this.store.select(BookingSelectors.getCurrencyOfProPackage());
  getSelectedInstallationsPlatformsWithPriceData$ = this.store.select(
    BookingSelectors.getSelectedInstallationsPlatformsWithPriceData
  );

  activeOrCanceledFlatRateSubscription$ = this.store.select(
    BookingSelectors.activeOrCanceledFlatRateSubscription
  );
  activeOrCanceledCommercialSubscription$ = this.store.select(
    BookingSelectors.activeOrCanceledCommercialSubscription
  );
  activeOrCanceledCommercialBasicSubscription$ = this.store.select(
    BookingSelectors.activeOrCanceledBasicCommercialSubscription
  );
  activeVoucherSubscription$ = this.store.select(
    BookingSelectors.selectActiveOrRenewVoucherSubscription
  );
  expiredVoucherSubscriptions$ = this.store.select(
    BookingSelectors.selectExpiredVoucherSubscriptions
  );
  expiredFlatRateSubscriptions$ = this.store.select(BookingSelectors.expiredFlatRateSubscriptions);
  expiredCommercialSubscriptions$ = this.store.select(
    BookingSelectors.expiredCommercialSubscriptions
  );
  getActiveProSubscriptionForFlatRate$ = this.store.select(
    BookingSelectors.getActiveProSubscriptionForFlatRate
  );
  getActiveTrialSubscriptionForFlatRate$ = this.store.select(
    BookingSelectors.getActiveTrialSubscriptionForFlatRate
  );
  subscriptionsLoaded$ = this.store.select(BookingSelectors.subscriptionsLoaded);
  getCancelSubscriptionStatus$ = this.store.select(BookingSelectors.getCancelSubscriptionStatus);
  getVoucherValidationState$ = this.store.select(BookingSelectors.getVoucherValidationState());
  showPlantOverviewLink$ = this.store.select(BookingSelectors.showPlantOverviewLink);
  bookingDevicesWithFilter$ = this.store.select(BookingSelectors.selectBookingDevicesWithFilter);
  bookingDevicesWithFilterAndPagination$ = this.store.select(
    BookingSelectors.selectBookingDevicesWithFilterAndPagination
  );
  searchInputValue$ = this.store.select(BookingSelectors.selectSearchInputValue);
  pagination$ = this.store.select(BookingSelectors.selectPagination);
  getBookingInstallations$ = this.store.select(BookingSelectors.getBookingInstallation());
  isBookingDevicesInInitState$ = this.store.select(
    BookingSelectors.selectIsBookingDevicesInInitState
  );
  isBookingDevicesLoading$ = this.store.select(BookingSelectors.selectIsBookingDevicesLoading);

  constructor(private store: Store, private installationsStore: InstallationsStore) {}

  loadInstallations(): void {
    this.installationsStore.installations$.pipe(first()).subscribe((installations) => {
      if (installations.length === 0) {
        this.installationsStore.listInstallations();
      }
    });
  }

  resetUserSelection(): void {
    this.store.dispatch(BookingActions.resetUserSelection());
  }

  loadBookingInstallation(): void {
    this.store.dispatch(BookingActions.loadBookingInstallations.attempt());
  }

  selectPlan(plan: SubscriptionPlan): void {
    this.store.dispatch(BookingActions.selectBookingPlan({ plan }));
  }

  subscribePackage(invoiceNumber?: string): void {
    this.store.dispatch(BookingActions.subscribePackage({ invoiceNumber }));
  }

  subscribeFlatRatePackage(): void {
    this.store.dispatch(BookingActions.subscribeFlatRatePackage());
  }

  subscribeCommercialPackage(): void {
    this.store.dispatch(BookingActions.subscribeCommercialPackage());
  }

  toggleInstallationSelection(deviceSerial: string): void {
    this.store.dispatch(BookingActions.toggleInstallationSelection({ deviceSerial }));
  }

  isSelectedInstallation(deviceSerial: string): Observable<boolean> {
    return this.store.select(BookingSelectors.isSelectedInstallation(deviceSerial));
  }

  selectInstallations(deviceSerials: string[]): void {
    this.store.dispatch(BookingActions.selectInstallations({ deviceSerials }));
  }

  deselectInstallations(deviceSerials: string[]): void {
    this.store.dispatch(BookingActions.deselectInstallations({ deviceSerials }));
  }

  triggerSelectInstallations(deviceSerials: string[]): void {
    this.store.dispatch(BookingActions.triggerSelectInstallations({ deviceSerials }));
  }

  triggerDeselectInstallations(deviceSerials: string[]): void {
    this.store.dispatch(BookingActions.triggerDeselectInstallations({ deviceSerials }));
  }

  selectInstallationsByOnlineDevicesWithActiveTrial(): void {
    this.store.dispatch(BookingActions.selectByOnlineDevicesWithActiveTrial());
  }

  fetchSubscriptionsPlans(): void {
    this.store.dispatch(BookingActions.fetchSubscriptionsPlans());
  }

  getAmountOfAvailableVouchers(invoiceNumber: string): Observable<number> {
    return this.store.select(BookingSelectors.getAmountOfAvailableVouchers(invoiceNumber));
  }

  getAllVitoguideSubscriptions(companyId?: string): void {
    this.store.dispatch(BookingActions.getVitoguideSubscriptions({ companyId }));
  }

  updateVitoguideSubscription(): void {
    this.store.dispatch(BookingActions.updateVitoguideSubscription());
  }

  cancelVitoguideSubscription(
    cancelReasonCode: string,
    boilerSerial: string,
    additionalComment: string,
    companyId?: string
  ): void {
    this.store.dispatch(
      BookingActions.cancelVitoguideSubscription({
        cancelReasonCode,
        boilerSerial,
        additionalComment,
        companyId,
      })
    );
  }

  createTrialSubscription(): void {
    this.store.dispatch(BookingActions.createTrialSubscription());
  }

  getTrialSubscriptions(): void {
    this.store.dispatch(BookingActions.getTrialSubscriptions());
  }

  subscribeVoucherPackage(voucher: string, installationId?: string): void {
    this.store.dispatch(BookingActions.subscribeVoucherPackage({ voucher, installationId }));
  }

  validateVoucherPackage(voucher: string): void {
    this.store.dispatch(BookingActions.validateVoucherPackage({ voucher }));
  }

  getContingentSubscriptions(): void {
    this.store.dispatch(BookingActions.getContingentSubscriptions());
  }

  getSubscriptionsDetails(boilerSerial?: string, update?: boolean): void {
    this.store.dispatch(BookingActions.getSubscriptionsDetails({ boilerSerial, update }));
  }

  resetSubscriptionsDetails(): void {
    this.store.dispatch(BookingActions.resetSubscriptionsDetails());
  }

  simulateSubscription(boilerSerial: string, companyId: string): void {
    this.store.dispatch(BookingActions.simulateSubscription({ boilerSerial, companyId }));
  }

  flatRateCancelSubscription(
    cancelReasonCode: string,
    additionalComment: string,
    companyId?: string
  ): void {
    this.store.dispatch(
      BookingActions.flatRateCancelSubscription({ cancelReasonCode, additionalComment, companyId })
    );
  }

  getFlatRatePriceDetails(): void {
    this.store.dispatch(BookingActions.flatRateGetPackagePriceDetails());
  }

  //TODO To be refactored after BTPD-12437
  getCommercialPriceDetails(subscriptionPlan: SubscriptionPlan.Basic | SubscriptionPlan.Pro): void {
    this.store.dispatch(BookingActions.commercialGetPackagePriceDetails({ subscriptionPlan }));
  }
  resetCancelSubscriptionStatus(): void {
    this.store.dispatch(BookingActions.resetCancelSubscriptionStatus());
  }

  selectBookableDevicesOfGivenInstallation(
    installationId: number
  ): Observable<BookingDashboardDevice[]> {
    return this.store.select(
      BookingSelectors.selectBookableDevicesOfGivenInstallation(installationId)
    );
  }

  selectBoilerSerialsOfBookableDevicesOfGivenInstallation(
    installationId: number
  ): Observable<string[]> {
    return this.store.select(
      BookingSelectors.selectBoilerSerialsOfBookableDevicesOfGivenInstallation(installationId)
    );
  }

  canVoucherBeRenew(boilerSerial: string): Observable<boolean> {
    return this.store.select(BookingSelectors.selectIfActiveVoucherSubCanBeRenew(boilerSerial));
  }

  setBookingPaginationPage(page: number): void {
    this.store.dispatch(BookingActions.setBookingPaginationPage({ page }));
  }

  setSearchInputValue(searchInput: string): void {
    this.store.dispatch(BookingActions.setSearchInputValue({ searchInput }));
  }

  clearBookingState(): void {
    this.store.dispatch(BookingActions.clearBookingState());
  }
}
